import React from 'react'
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { EnrollmentState } from 'src/graphql.generated'
import { ALL_STATES } from 'src/participant/participantConstants'
import { useGetAdvancedAllTagsQuery } from '../api/advancedPatientTags.generated'
import { useCoachRoster } from '../api/useCoachRoster'
import { useGetAdvancedAdminSourcesQuery } from './adminCareTeams.generated'
import { ENROLLMENT_STATES } from './enrollmentStates'

export type AdminPatientFilter = {
  query: string
  coachIDs: string[]
  primaryOnly: boolean
  states: EnrollmentState[]
  sources: string[]
  statesOfResidence: string[]
  tagIDs: string[]
}

type Option = { id: string; label: string }

export const EMPTY_FILTER: AdminPatientFilter = {
  query: '',
  coachIDs: [],
  primaryOnly: false,
  states: [],
  sources: [],
  statesOfResidence: [],
  tagIDs: [],
}

const MIN_QUERY_LENGTH = 2

const RESIDENCE_STATES: Option[] = ALL_STATES.map((state) => ({
  id: state.code,
  label: `${state.name} (${state.code})`,
}))

export const hasFilter = (filter: AdminPatientFilter): boolean =>
  filter.query.trim().length >= MIN_QUERY_LENGTH ||
  [filter.coachIDs, filter.states, filter.sources, filter.statesOfResidence, filter.tagIDs].some(
    (selected) => selected.length > 0
  )

const MultiFilter = <T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { id: T; label: string }[]
  value: T[]
  onChange: (ids: T[]) => void
}) => (
  <Autocomplete
    multiple
    fullWidth
    size="small"
    sx={{ minWidth: 0 }}
    options={options}
    value={options.filter((option) => value.includes(option.id))}
    onChange={(_event, selected) => onChange(selected.map((option) => option.id))}
    getOptionLabel={(option) => option.label}
    isOptionEqualToValue={(option, selected) => option.id === selected.id}
    renderInput={(params) => <TextField {...params} label={label} />}
  />
)

type Props = { filter: AdminPatientFilter; onChange: (filter: AdminPatientFilter) => void }

const AdminPatientFilters = ({ filter, onChange }: Props) => {
  const coaches = useCoachRoster()
  const { data: sourcesData } = useGetAdvancedAdminSourcesQuery()
  const { data: tagsData } = useGetAdvancedAllTagsQuery()
  const set = (change: Partial<AdminPatientFilter>) => onChange({ ...filter, ...change })

  const coachOptions = coaches.map((coach) => ({ id: coach.id, label: coach.name }))
  const sourceOptions = (sourcesData?.sources ?? []).map((source) => ({ id: source.name, label: source.name }))
  const tagOptions = (tagsData?.patientTags ?? []).map((tag) => ({ id: tag.id, label: tag.name }))

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}
    >
      <TextField
        fullWidth
        id="advanced-admin-care-team-query"
        label="Name, email or phone"
        size="small"
        value={filter.query}
        onChange={(event) => set({ query: event.target.value })}
        sx={{ gridColumn: '1 / -1' }}
      />
      <MultiFilter
        label="Enrollment state"
        options={ENROLLMENT_STATES}
        value={filter.states}
        onChange={(states) => set({ states })}
      />
      <MultiFilter
        label="Source"
        options={sourceOptions}
        value={filter.sources}
        onChange={(sources) => set({ sources })}
      />
      <MultiFilter
        label="State of residence"
        options={RESIDENCE_STATES}
        value={filter.statesOfResidence}
        onChange={(statesOfResidence) => set({ statesOfResidence })}
      />
      <MultiFilter label="Tags" options={tagOptions} value={filter.tagIDs} onChange={(tagIDs) => set({ tagIDs })} />
      <Box sx={{ gridColumn: { md: 'span 2' }, minWidth: 0 }}>
        <MultiFilter
          label="Standard coach"
          options={coachOptions}
          value={filter.coachIDs}
          onChange={(coachIDs) => set({ coachIDs })}
        />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={filter.primaryOnly}
            disabled={filter.coachIDs.length === 0}
            onChange={(event) => set({ primaryOnly: event.target.checked })}
          />
        }
        label="Must be primary coach"
        sx={{ gridColumn: { md: 'span 2' }, m: 0, minWidth: 0, alignSelf: 'center' }}
      />
    </Box>
  )
}

export default AdminPatientFilters
